import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User } from '../../../types';
import '../styles/ParentInfoModal.css';

interface Parent extends User {}

interface LinkedStudent {
  id: string;
  displayName: string;
  email: string;
  relationship: 'parent' | 'guardian' | 'other';
  linkedAt: any;
}

interface ConsentEvent {
  type: 'tos' | 'photoRelease';
  version: string;
  url: string;
  value: boolean | 'accept' | 'deny';
  timestamp: string;
  ip?: string;
  userAgent?: string;
}

interface ParentInfoModalProps {
  parent: Parent;
  onClose: () => void;
  onParentUpdated: () => void;
}

const ParentInfoModal: React.FC<ParentInfoModalProps> = ({ parent, onClose, onParentUpdated }) => {
  const [parentDetails, setParentDetails] = useState<Parent | null>(null);
  const [linkedStudents, setLinkedStudents] = useState<LinkedStudent[]>([]);
  const [consentHistory, setConsentHistory] = useState<ConsentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'students' | 'consents'>('info');

  useEffect(() => {
    loadParentDetails();
  }, [parent.id]);

  const loadParentDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get parent details from Firestore
      const parentDoc = await getDoc(doc(db, 'users', parent.id));
      if (!parentDoc.exists()) {
        throw new Error('Parent not found');
      }
      
      const parentData = { id: parentDoc.id, ...parentDoc.data() } as User;
      setParentDetails(parentData);

      // Get linked students
      const linksRef = collection(db, 'parentStudentLinks');
      const linksQuery = query(linksRef, where('parentId', '==', parent.id));
      const linksSnapshot = await getDocs(linksQuery);
      
      const linkedStudentsList: LinkedStudent[] = [];
      for (const linkDoc of linksSnapshot.docs) {
        const linkData = linkDoc.data();
        
        // Get student details
        const studentDoc = await getDoc(doc(db, 'users', linkData.studentId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          linkedStudentsList.push({
            id: linkData.studentId,
            displayName: studentData.displayName,
            email: studentData.email,
            relationship: linkData.relationship,
            linkedAt: linkData.createdAt
          });
        }
      }
      
      setLinkedStudents(linkedStudentsList);

      // Get consent history
      const consentEventsRef = collection(db, 'users', parent.id, 'consentEvents');
      const consentQuery = query(consentEventsRef, orderBy('timestamp', 'desc'));
      const consentSnapshot = await getDocs(consentQuery);
      
      const consentHistoryList: ConsentEvent[] = consentSnapshot.docs.map(doc => doc.data() as ConsentEvent);
      setConsentHistory(consentHistoryList);

    } catch (err) {
      console.error('Error loading parent details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load parent details');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkStudent = async (studentId: string) => {
    if (!await window.appConfirm('Are you sure you want to unlink this student from the parent?')) {
      return;
    }

    try {
      // Find and delete the parent-student link
      const linksRef = collection(db, 'parentStudentLinks');
      const linksQuery = query(
        linksRef, 
        where('parentId', '==', parent.id),
        where('studentId', '==', studentId)
      );
      const linksSnapshot = await getDocs(linksQuery);
      
      if (linksSnapshot.empty) {
        throw new Error('Parent-student link not found');
      }

      // Delete the link document
      await deleteDoc(linksSnapshot.docs[0].ref);

      // Check if student has other parent links
      const otherLinksQuery = query(linksRef, where('studentId', '==', studentId));
      const otherLinksSnapshot = await getDocs(otherLinksQuery);

      // If no other parent links, remove parentEmail from student
      if (otherLinksSnapshot.empty) {
        const studentRef = doc(db, 'users', studentId);
        const studentDoc = await getDoc(studentRef);
        if (studentDoc.exists()) {
          // We can't use updateDoc to delete a field in admin context, 
          // so we'll leave this for now and handle via Cloud Functions if needed
          console.log('Student has no more parent links, parentEmail should be cleared');
        }
      }

      // Refresh parent details
      loadParentDetails();
      onParentUpdated();

    } catch (err) {
      console.error('Error unlinking student:', err);
      void window.appAlert(`Failed to unlink student: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const timestamp = typeof date === 'string' ? new Date(date) : 
                     (date.seconds ? new Date(date.seconds * 1000) : new Date(date));
    return timestamp.toLocaleDateString() + ' ' + timestamp.toLocaleTimeString();
  };

  const formatDateOnly = (date: any) => {
    if (!date) return 'N/A';
    const timestamp = typeof date === 'string' ? new Date(date) : 
                     (date.seconds ? new Date(date.seconds * 1000) : new Date(date));
    return timestamp.toLocaleDateString();
  };

  const getSchoolDisplayName = (slug: string) => {
    const schoolMap: { [key: string]: string } = {
      'lincoln-elementary': 'Lincoln Elementary',
      'roosevelt-middle': 'Roosevelt Middle School',
      'washington-high': 'Washington High School',
      'jefferson-academy': 'Jefferson Academy',
      'other': 'Other'
    };
    return schoolMap[slug] || slug;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'suspended': return 'status-badge status-suspended';
      case 'invited': return 'status-badge status-invited';
      default: return 'status-badge';
    }
  };

  const renderInfoTab = () => {
    if (!parentDetails) return null;

    return (
      <div className="info-tab">
        <div className="info-grid">
          <div className="info-card">
            <h4>Basic Information</h4>
            <div className="info-row">
              <label>Full Name:</label>
              <span>{parentDetails.displayName}</span>
            </div>
            <div className="info-row">
              <label>Email:</label>
              <span>{parentDetails.email}</span>
            </div>
            <div className="info-row">
              <label>School:</label>
              <span>{getSchoolDisplayName(parentDetails.schoolSlug || '')}</span>
            </div>
            <div className="info-row">
              <label>Status:</label>
              <span className={getStatusBadgeClass(parentDetails.status || 'active')}>
                {parentDetails.status || 'active'}
              </span>
            </div>
          </div>

          <div className="info-card">
            <h4>Account Details</h4>
            <div className="info-row">
              <label>Created:</label>
              <span>{formatDate(parentDetails.createdAt)}</span>
            </div>
            <div className="info-row">
              <label>Last Login:</label>
              <span>{formatDate(parentDetails.lastLogin)}</span>
            </div>
            <div className="info-row">
              <label>Parent ID:</label>
              <span className="parent-id">{parentDetails.id}</span>
            </div>
          </div>
        </div>

        <div className="current-consents">
          <h4>Current Consents</h4>
          <div className="consent-cards">
            <div className="consent-card">
              <div className="consent-header">
                <span className="consent-title">Terms of Service</span>
                <span className={`consent-status ${parentDetails.consents?.tos?.accepted ? 'accepted' : 'not-accepted'}`}>
                  {parentDetails.consents?.tos?.accepted ? '✅ Accepted' : '❌ Not Accepted'}
                </span>
              </div>
              {parentDetails.consents?.tos && (
                <div className="consent-details">
                  <div>Version: {parentDetails.consents.tos.version}</div>
                  <div>Date: {formatDateOnly(parentDetails.consents.tos.acceptedAt)}</div>
                </div>
              )}
            </div>

            <div className="consent-card">
              <div className="consent-header">
                <span className="consent-title">Photo/Video Release</span>
                <span className={`consent-status ${parentDetails.consents?.photoRelease?.value === 'accept' ? 'accepted' : 'denied'}`}>
                  {parentDetails.consents?.photoRelease?.value === 'accept' ? '✅ Permitted' : '❌ Denied'}
                </span>
              </div>
              {parentDetails.consents?.photoRelease && (
                <div className="consent-details">
                  <div>Version: {parentDetails.consents.photoRelease.version}</div>
                  <div>Date: {formatDateOnly(parentDetails.consents.photoRelease.updatedAt)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStudentsTab = () => {
    return (
      <div className="students-tab">
        <div className="students-header">
          <h4>Linked Students ({linkedStudents.length})</h4>
        </div>

        {linkedStudents.length === 0 ? (
          <div className="no-students">
            <p>No students are currently linked to this parent account.</p>
          </div>
        ) : (
          <div className="students-list">
            {linkedStudents.map(student => (
              <div key={student.id} className="student-card">
                <div className="student-info">
                  <div className="student-name">{student.displayName}</div>
                  <div className="student-email">{student.email}</div>
                  <div className="relationship">
                    Relationship: <span className="relationship-badge">{student.relationship}</span>
                  </div>
                  <div className="linked-date">Linked: {formatDate(student.linkedAt)}</div>
                </div>
                <div className="student-actions">
                  <button
                    onClick={() => handleUnlinkStudent(student.id)}
                    className="unlink-button"
                    title="Unlink this student"
                  >
                    🔗 Unlink
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderConsentsTab = () => {
    return (
      <div className="consents-tab">
        <div className="consents-header">
          <h4>Consent History ({consentHistory.length} events)</h4>
          <p>Complete audit trail of all consent changes (read-only)</p>
        </div>

        {consentHistory.length === 0 ? (
          <div className="no-consents">
            <p>No consent events recorded.</p>
          </div>
        ) : (
          <div className="consents-timeline">
            {consentHistory.map((event, index) => (
              <div key={index} className="consent-event">
                <div className="event-header">
                  <span className="event-type">{event.type === 'tos' ? 'Terms of Service' : 'Photo/Video Release'}</span>
                  <span className="event-date">{formatDate(event.timestamp)}</span>
                </div>
                <div className="event-details">
                  <div className="event-value">
                    Value: <span className={`value-badge ${event.value === true || event.value === 'accept' ? 'positive' : 'negative'}`}>
                      {event.value === true ? 'Accepted' : 
                       event.value === 'accept' ? 'Permitted' :
                       event.value === 'deny' ? 'Denied' : 'Declined'}
                    </span>
                  </div>
                  <div className="event-version">Version: {event.version}</div>
                  {event.ip && <div className="event-ip">IP: {event.ip}</div>}
                  {event.userAgent && (
                    <div className="event-useragent">
                      Browser: {event.userAgent.split(' ')[0]}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="parent-info-modal">
          <div className="modal-header">
            <h3>Loading Parent Details...</h3>
            <button onClick={onClose} className="close-button">✕</button>
          </div>
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="parent-info-modal">
          <div className="modal-header">
            <h3>Error</h3>
            <button onClick={onClose} className="close-button">✕</button>
          </div>
          <div className="error-content">
            <p>{error}</p>
            <button onClick={loadParentDetails} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="parent-info-modal">
        <div className="modal-header">
          <div className="header-info">
            <h3>{parent.displayName}</h3>
            <span className="parent-email">{parent.email}</span>
          </div>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        <div className="modal-tabs">
          <button
            onClick={() => setActiveTab('info')}
            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
          >
            ℹ️ Info
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
          >
            👨‍🎓 Students ({linkedStudents.length})
          </button>
          <button
            onClick={() => setActiveTab('consents')}
            className={`tab-button ${activeTab === 'consents' ? 'active' : ''}`}
          >
            📝 Consents ({consentHistory.length})
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'info' && renderInfoTab()}
          {activeTab === 'students' && renderStudentsTab()}
          {activeTab === 'consents' && renderConsentsTab()}
        </div>
      </div>
    </div>
  );
};

export default ParentInfoModal;